import _ from 'lodash';
import React from 'react';

import Avatar from 'components/Avatar';
import {default as Modal, withModal} from 'components/Modal';
import Button from 'components/Button';
import Loading from 'components/Loading';
import Loader from 'components/Loader';
import {
  ButtonDropdown,
  SearchInput
} from 'containers/MyTeams/components/shared';
import {withFilters, FILTERS} from 'components/withFilters';

import {
  MODALS,
  fetchTeamDetail,
  fetchStaffMembers,
  selectStaffMembers,
  addMemberToTeam,
  removeMemberFromTeam,
  demoteTeamOwner,
  promoteTeamOwner,
  fetchAllTeams,
  fetchMyTeams
} from 'ducks/teams';
import {
  requiresPermission,
  requiresPermissionOrTeamOwnership,
  PERMISSIONS
} from 'ducks/auth';

import {
  MODALS as BASE_MODALS,
  openModal,
  closeModal,
  storeDataInModal
} from 'ducks/modals';

import './styles.css';

const initialFilters = {
  search: ''
};

const TeamMemberRow = ({member, onDemote, onRemove, onPromote}) => (
  <div className="memberRow">
    <Avatar
      url={member.internalAvatarCropped || member.avatarCropped}
      initials={member.initials}
    />
    <div className="info">
      <div className="name">{member.fullName}</div>
      <div className="info">
        {member.isOwner && <div>Team Owner</div>}
        {!member.isOwner && <div>Team Member</div>}
      </div>
    </div>
    <ButtonDropdown
      className="modded"
      title="Edit"
      menuPosition={ButtonDropdown.MENU_POSITION_BOTTOM_RIGHT}
      appendToBody={true}>
      {!member.isOwner && (
        <ButtonDropdown.Option onClick={() => onPromote(member)}>
          Promote to team owner
        </ButtonDropdown.Option>
      )}
      {member.isOwner && (
        <ButtonDropdown.Option onClick={() => onDemote(member)}>
          Demote to team member
        </ButtonDropdown.Option>
      )}
      <ButtonDropdown.Option onClick={() => onRemove(member)} className="red">
        Remove from team
      </ButtonDropdown.Option>
    </ButtonDropdown>
  </div>
);

const StaffMemberRow = ({team, member, onClick}) => (
  <div className="memberRow">
    <Avatar
      url={member.internalAvatarCropped || member.avatarCropped}
      initials={member.initials}
    />
    <div className="info">
      <div className="name">{member.name}</div>
      <div className="info">{member.jobTitle}</div>
    </div>
    {team && (
      <Button white onClick={() => onClick({memberId: member.id})}>
        Add
      </Button>
    )}
  </div>
);

class ManageMembersModal extends React.Component<any> {
  state = {
    search: {value: ''}
  };

  componentDidMount() {
    const {teamId} = this.props.data;

    this.props.fetchStaffMembers(this.state);
    this.props.fetchTeamDetail(
      {teamId},
      this.props.storeDataExtraActions(data => ({team: data}))
    );
  }

  componentWillUnmount() {
    // We don't want the filters from this modal to affect the `AllStaffMembersTable` data.
    this.props.fetchStaffMembers({filters: {}});
  }

  getExtraActions = () => {
    const {team} = this.props.data;

    return [
      () => this.props.fetchStaffMembers({filters: this.state}),
      () =>
        this.props.fetchTeamDetail({teamId: team.id}, [
          ...this.props.storeDataExtraActions(data => ({team: data})),
          data =>
            this.props.storeDataInModal({
              modalName: MODALS.TEAM_DETAILS,
              data: {
                team: data
              }
            })
        ]),
      fetchAllTeams,
      fetchMyTeams
    ];
  };

  // This adds a custom "isLoading" to indicate that the data hasn't been fetched yet.
  // It should be used in all update actions
  // The "isLoading" will be removed once the fetchTeamDetails call has been done
  updateTeamExtraActions = () => {
    const {team} = this.props.data;

    return [
      ...this.getExtraActions(),
      ...this.props.storeDataExtraActions(() => ({
        team: {
          ...team,
          isLoading: true
        }
      }))
    ];
  };

  separateMembers = (members, team) => {
    const currentMembers = _.get(team, 'members', []);
    const otherMembers = _.filter(
      members,
      member => currentMembers.filter(x => x.id === member.id).length === 0
    );

    return [currentMembers, otherMembers];
  };

  addTeamMember = ({memberId}) => {
    const {team} = this.props.data;

    this.props.requiresPermissionOrTeamOwnership(
      {teamId: team.id, permission: PERMISSIONS.ADD_TEAM_MEMBERS},
      () =>
        this.props.addMemberToTeam(
          {memberId, teamId: team.id},
          this.updateTeamExtraActions()
        )
    );
  };

  demoteTeamOwner = member =>
    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_TEAM_MEMBERS},
      () =>
        this.props.demoteTeamOwner(
          {memberId: member.id, teamId: this.props.data.team.id},
          this.updateTeamExtraActions()
        )
    );

  promoteToTeamOwner = member => {
    this.props.promoteTeamOwner(
      {memberId: member.id, teamId: this.props.data.team.id},
      this.updateTeamExtraActions()
    );
  };

  removeTeamMember = member => {
    const {team} = this.props.data;
    const confirmAction = () =>
      this.props.removeMemberFromTeam({memberId: member.id, teamId: team.id}, [
        ...this.updateTeamExtraActions(),
        () => closeModal(BASE_MODALS.CONFIRM)
      ]);

    const question = (
      <div>
        Are you sure you want to remove <b>{member.name}</b> from the team{' '}
        <b>{team.name}</b>?
      </div>
    );

    const hasPermissionFunc = () =>
      this.props.openModal(BASE_MODALS.CONFIRM, {
        danger: true,
        heading: 'Remove from Team',
        question,
        confirmAction
      });

    /*
     1) Admin from a team with permission/Superuser *can* remove team members and team owners
     2) Team owners *can* remove team members from teams where they are owners
     3) Team owners *cannot* remove team owners from teams where they are owners
     4) Team owners *cannot* remove team members/owners from teams where they are not owners
    */

    if (member.isOwner) {
      this.props.requiresPermission(
        {permission: PERMISSIONS.REMOVE_TEAM_MEMBERS},
        hasPermissionFunc
      );
    } else {
      this.props.requiresPermissionOrTeamOwnership(
        {permission: PERMISSIONS.REMOVE_TEAM_MEMBERS, teamId: team.id},
        hasPermissionFunc
      );
    }
  };

  openInviteNewTeamMemberModal = () => {
    const {team} = this.props.data;

    this.props.requiresPermissionOrTeamOwnership(
      {teamId: team.id, permission: PERMISSIONS.INVITE_NEW_STAFF_MEMBER},
      () =>
        this.props.openModal(MODALS.INVITE_NEW_TEAM_MEMBER, {
          teamId: this.props.data.teamId,
          getParrentModalExtraActions: this.getExtraActions
        })
    );
  };

  debouncedSearch = _.debounce(
    () => this.props.searchStaffMembers(this.state.search.value),
    200
  );

  onSearchChange = event => {
    const {target: {value}} = event;

    this.props.setFilter('search', value);
    this.setState({search: {value}}, this.debouncedSearch);
  };

  render() {
    const {team, members} = this.props.data;
    const [currentMembers, otherMembers] = this.separateMembers(members, team);

    return (
      <Modal
        className="ManageMembersModal"
        type={Modal.EXTRA_SMALL}
        show={true}
        onClose={this.props.closeModal}>
        <Modal.Header heading="Manage Members" />
        <Modal.Body>
          {_.isNil(team) && <Loader />}
          {!_.isNil(team) && (
            <React.Fragment>
              <SearchInput
                placeholder="Search by name or email"
                onSearchChange={this.onSearchChange}
              />
              <div className="content">
                <div className="heading">Team Members</div>
                {_.get(team, 'isLoading') && <Loading />}
                <div className="container">
                  {_.isEmpty(currentMembers) &&
                    !_.isNil(team) && (
                      <div className="noMembersMessage">
                        This team has no team members yet. Add the first one!
                      </div>
                    )}
                  {currentMembers.map(member => (
                    <TeamMemberRow
                      key={member.id}
                      member={member}
                      onDemote={this.demoteTeamOwner}
                      onRemove={this.removeTeamMember}
                      onPromote={this.promoteToTeamOwner}
                    />
                  ))}
                </div>
                <div className="heading">Other Staff Members</div>
                <div className="container">
                  {_.isEmpty(otherMembers) && (
                    <div className="noMembersMessage">
                      All staff members are part of this team. Invite a new one!
                    </div>
                  )}
                  {!_.isNil(team) &&
                    otherMembers.map(member => (
                      <StaffMemberRow
                        key={member.id}
                        team={team}
                        member={member}
                        onClick={this.addTeamMember}
                      />
                    ))}
                </div>
              </div>
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          {team && (
            <Button white onClick={this.openInviteNewTeamMemberModal}>
              Invite new
            </Button>
          )}
          <Button white onClick={this.props.closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = state => {
  return {members: selectStaffMembers(state)};
};

const mapDispatchToProps = {
  fetchStaffMembers,
  fetchTeamDetail,
  addMemberToTeam,
  removeMemberFromTeam,
  demoteTeamOwner,
  promoteTeamOwner,
  openModal,
  storeDataInModal,
  requiresPermission,
  requiresPermissionOrTeamOwnership
};

const componentWithFilters = withFilters(
  ManageMembersModal,
  FILTERS.MY_TEAMS_MEMBERS,
  {initialFilters, saveToUrl: true}
);

export default withModal(
  MODALS.MANAGE_MEMBERS,
  mapStateToProps,
  mapDispatchToProps
)(componentWithFilters);
